import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';
import { getTulpiaanToken } from '../utils/token';

// =============================================================================
// TULPIAAN — METHODIEK-CONFIGURATIE
// =============================================================================

const TUL_PROMPT = `Je bent een salestrainer bij Tulpiaan. Je helpt adviseurs om klanten echt te begrijpen voordat ze een oplossing presenteren.

TOON EN STIJL:
- Schrijf vragen zoals je ze in een echt gesprek stelt. Direct, menselijk, zonder omhaal.
- Gebruik altijd "je" en "jij", nooit "u".
- Korte zinnen. Splits lange vragen op.
- Geen jargon, geen formele formuleringen.
- Verboden woorden: "kunt u beschrijven", "zou u kunnen toelichten", "welke problematiek", "omtrent", "teneinde".
- Goede voorbeeldvraag: "Waar loop je nu het meest tegenaan?"
- Slechte voorbeeldvraag: "Kunt u de voornaamste operationele uitdagingen beschrijven waarmee uw organisatie momenteel geconfronteerd wordt?"

KERNPRINCIPES:
1. Eerst begrijpen, dan pas praten over oplossingen. Geen tips, geen suggesties in deze fase.
2. Elke vraag voelt alsof je de klant echt wil helpen, niet alsof je iets wil verkopen.
3. Focus op bewijs, impact en urgentie. Geen oppervlakkige vragen.
4. Nooit leidende vragen. De klant trekt zijn eigen conclusies.

DE TULPIAAN TUL-STRUCTUUR:

T = TOEKOMST (Waar wil de klant naartoe?)
Begin altijd positief. Begrijp eerst de droom, de ambitie, het doel.
- Ambities: Wat wil de klant bereiken? Waar wil het bedrijf over 2-3 jaar staan?
- Gewenste situatie: Hoe ziet succes er voor hen uit? Wat moet er anders zijn?
- Resultaat: Wanneer is het gelukt? Hoe meten ze dat?

U = UITDAGINGEN (Wat staat er tussen nu en die toekomst in?)
Nu pas ga je naar de pijn. Wat blokkeert hen?
- Problemen: Waar lopen ze tegenaan? Wat werkt niet?
- Bewijs: Hoe weten ze dat het een probleem is? Wat meten ze?
- Impact: Wat kost het ze? Vraag door: wat is het nu, wat zou beter zijn, wat is het verschil waard, ook over 2-3 jaar?
- Beperkingen: Waarom is dit nog niet opgelost? Wat heeft hen tegengehouden?

L = LEVERING (Kunnen ze het waarmaken, en hoe werken we samen?)
Twee lagen, beide even belangrijk:

Laag 1 - Resources:
- Tijd: Wanneer willen ze beginnen? Wanneer moet er resultaat zijn?
- Budget: Is er al een budget? Wat past bij de verwachte opbrengst?
- Besluitvorming: Wie beslist? Welke stappen nemen ze? Wanneer?

Laag 2 - Samenwerking:
- Inzet: Hoeveel tijd en energie willen ze er zelf in steken?
- Taakverdeling: Wat doen zij, wat doen wij? Hoe ziet de verhouding eruit, bijv. 70/30?
- Mensen: Wie van hun kant werkt er actief mee? Wie is de interne trekker?
- Verantwoordelijkheid: Hoe zien ze de verantwoordelijkheidsverdeling?

OUTPUT INSTRUCTIES:
Genereer 15 tot 30 vragen, verdeeld over T, U en L.
Pas elke vraag aan op: de branche, wat de klant doet, de rol van de gesprekspartner, de complexiteit.

Per vraag:
- De vraag zelf: conversationeel, direct, zoals je het in een gesprek zegt
- De waarom: 1 tot 2 zinnen, laat zien waarom deze vraag belangrijk is

Geef ALLEEN de volgende JSON terug, geen uitleg, geen markdown:
{
  "samenvatting": "3 tot 4 zinnen over hoe de adviseur dit gesprek het beste aanvliegt",
  "toekomst": {
    "ambities": [{"vraag": "...", "waarom": "..."}],
    "gewenste_situatie": [{"vraag": "...", "waarom": "..."}],
    "resultaat": [{"vraag": "...", "waarom": "..."}]
  },
  "uitdagingen": {
    "problemen": [{"vraag": "...", "waarom": "..."}],
    "bewijs_en_impact": [{"vraag": "...", "waarom": "..."}],
    "beperkingen": [{"vraag": "...", "waarom": "..."}]
  },
  "levering": {
    "resources_en_besluitvorming": [{"vraag": "...", "waarom": "..."}],
    "samenwerking": [{"vraag": "...", "waarom": "..."}]
  }
}`;

const P_PROMPT = `Je bent een salestrainer bij Tulpiaan. Een adviseur heeft zojuist zijn oplossing gepresenteerd aan een klant. Nu moet hij toetsvragen stellen om te controleren of de presentatie goed is geland.

TOON EN STIJL:
- Direct, conversationeel, menselijk. Gebruik "je" en "jij".
- Korte vragen. Geen formeel taalgebruik.

DOEL: Toetsen of de pitch is geland. Begrip, herkenning, wat ontbreekt, volgende stappen.

GEBRUIK DE TUL-CONTEXT om vragen te personaliseren op wat eerder is gezegd.

VASTE KERN: Wat spreekt je het meest aan? Wat is nog niet duidelijk? Herken je jezelf hierin? Wat zou je anders willen? Wat zijn de volgende stappen?

OUTPUT INSTRUCTIES:
Genereer 8 tot 12 toetsvragen. Verdeel over: begrip en herkenning, aansluiting op gesprek, volgende stappen.

Per vraag:
- De vraag zelf
- De waarom: 1 zin

Geef ALLEEN de volgende JSON terug:
{
  "intro": "1 tot 2 zinnen over hoe je de P-fase introduceert na de pitch",
  "presentatie": {
    "begrip_en_herkenning": [{"vraag": "...", "waarom": "..."}],
    "aansluiting_op_gesprek": [{"vraag": "...", "waarom": "..."}],
    "volgende_stappen": [{"vraag": "...", "waarom": "..."}]
  }
}`;

// =============================================================================
// KLEUREN
// =============================================================================
const C = {
  goud:'#cbad74', goudDiep:'#a8894f', goudLicht:'#f0e6cc',
  wit:'#ffffff', papier:'#faf9f6', grijs50:'#f5f4f1',
  grijs100:'#edecea', grijs200:'#d8d6d0', grijs400:'#9e9b93',
  grijs600:'#6b6860', grijs800:'#3a3830', inkt:'#1e1c17',
  fout:'#c04a3a', foutBg:'#fdf2f0',
  tBlauw:'#e8f0f8', tBlauwRand:'#b8cfe8', tTekst:'#3a6090',
  uOranje:'#fdf0e8', uOranjeRand:'#e8c8a0', uTekst:'#8a5020',
  lGroen:'#edf5ee', lGroenRand:'#a8cca8', lTekst:'#3a7040',
  pPaars:'#f0edf8', pPaarsRand:'#c0aee0', pTekst:'#5a3880',
};

const FASE_KLEUR = {
  toekomst:    { bg:C.tBlauw,  rand:C.tBlauwRand,  tekst:C.tTekst },
  uitdagingen: { bg:C.uOranje, rand:C.uOranjeRand,  tekst:C.uTekst },
  levering:    { bg:C.lGroen,  rand:C.lGroenRand,   tekst:C.lTekst },
  presentatie: { bg:C.pPaars,  rand:C.pPaarsRand,   tekst:C.pTekst },
};

const TUL_GROEP_LABELS = {
  ambities:'Ambities', gewenste_situatie:'Gewenste situatie', resultaat:'Resultaat',
  problemen:'Problemen', bewijs_en_impact:'Bewijs en impact', beperkingen:'Beperkingen',
  resources_en_besluitvorming:'Resources en besluitvorming', samenwerking:'Samenwerking',
};
const P_GROEP_LABELS = {
  begrip_en_herkenning:'Begrip en herkenning',
  aansluiting_op_gesprek:'Aansluiting op het gesprek',
  volgende_stappen:'Volgende stappen',
};
const TUL_CATS = [
  {key:'toekomst',    letter:'T', label:'Toekomst',    sub:'Waar wil de klant naartoe?'},
  {key:'uitdagingen', letter:'U', label:'Uitdagingen', sub:'Wat staat er tussen nu en de toekomst?'},
  {key:'levering',    letter:'L', label:'Levering',    sub:'Kunnen ze het waarmaken en hoe werken we samen?'},
];

// =============================================================================
// PDF GENERATOR (print-venster)
// =============================================================================
function genereerPdfHtml(form, tulData, pData, logoSrc) {
  const datum = new Date().toLocaleDateString('nl-NL', {day:'numeric', month:'long', year:'numeric'});

  const faseKleuren = {
    T: {bg:'#e8f0f8', rand:'#b8cfe8', tekst:'#3a6090', lichtBg:'#f0f5fb', accent:'#3a6090'},
    U: {bg:'#fdf0e8', rand:'#e8c8a0', tekst:'#8a5020', lichtBg:'#fef7f2', accent:'#8a5020'},
    L: {bg:'#edf5ee', rand:'#a8cca8', tekst:'#3a7040', lichtBg:'#f4faf4', accent:'#3a7040'},
    P: {bg:'#f0edf8', rand:'#c0aee0', tekst:'#5a3880', lichtBg:'#f7f4fc', accent:'#5a3880'},
  };

  const renderTulSectie = (letter, label, sub, data, groepLabels) => {
    const fk = faseKleuren[letter];
    let nr = 1;
    let html = `
      <div style="display:flex; align-items:stretch; margin:20px 0 0 0; break-before:auto;">
        <div style="width:4px; background:${fk.rand}; flex-shrink:0; border-radius:2px 0 0 0;"></div>
        <div style="background:${fk.bg}; padding:10px 16px; flex:1; display:flex; align-items:center; gap:12px;">
          <span style="font-family:Georgia,serif; font-size:32px; font-weight:700; color:${fk.tekst}; opacity:0.3; line-height:1;">${letter}</span>
          <div>
            <span style="font-family:Georgia,serif; font-size:15px; font-weight:600; color:${fk.tekst};">${label}</span>
            <span style="font-size:11px; color:#6b6860; margin-left:8px;">${sub}</span>
          </div>
        </div>
      </div>`;

    Object.entries(data).forEach(([groep, vragen]) => {
      const groepLabel = groepLabels[groep] || groep;
      html += `
        <table style="width:100%; border-collapse:collapse; margin-bottom:1px;">
          <thead>
            <tr style="background:${fk.lichtBg};">
              <td colspan="3" style="padding:5px 10px; border:1px solid ${fk.rand}; border-top:none;">
                <span style="font-size:9px; font-weight:700; color:${fk.tekst}; text-transform:uppercase; letter-spacing:0.12em;">${groepLabel}</span>
              </td>
            </tr>
            <tr style="background:#f5f4f1;">
              <th style="width:45%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none;">Vraag</th>
              <th style="width:30%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none; border-left:none;">Waarom</th>
              <th style="width:25%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none; border-left:none;">Antwoord</th>
            </tr>
          </thead>
          <tbody>`;

      vragen.forEach((v, i) => {
        const bg = i % 2 === 0 ? '#ffffff' : '#fafaf8';
        html += `
            <tr style="background:${bg}; vertical-align:top; page-break-inside:avoid;">
              <td style="padding:6px 10px; border:1px solid #edecea; border-top:none;">
                <span style="font-size:9px; font-weight:700; color:#cbad74; margin-right:5px;">${String(nr).padStart(2,'0')}</span><span style="font-family:Georgia,serif; font-size:12px; color:#1e1c17; line-height:1.45;">${v.vraag}</span>
              </td>
              <td style="padding:6px 10px; border:1px solid #edecea; border-top:none; border-left:none;">
                <span style="font-size:10px; color:#9e9b93; font-style:italic; line-height:1.4;">${v.waarom}</span>
              </td>
              <td style="padding:6px 10px; border:1px solid #edecea; border-top:none; border-left:none; background:#fdfdf9; min-height:36px;"></td>
            </tr>`;
        nr++;
      });
      html += `</tbody></table>`;
    });
    return html;
  };

  const renderPSectie = (data) => {
    const fk = faseKleuren['P'];
    let nr = 1;
    let html = `
      <div style="display:flex; align-items:stretch; margin:20px 0 0 0;">
        <div style="width:4px; background:${fk.rand}; flex-shrink:0; border-radius:2px 0 0 0;"></div>
        <div style="background:${fk.bg}; padding:10px 16px; flex:1; display:flex; align-items:center; gap:12px;">
          <span style="font-family:Georgia,serif; font-size:32px; font-weight:700; color:${fk.tekst}; opacity:0.3; line-height:1;">P</span>
          <div>
            <span style="font-family:Georgia,serif; font-size:15px; font-weight:600; color:${fk.tekst};">Presentatie</span>
            <span style="font-size:11px; color:#6b6860; margin-left:8px;">Toetsvragen na de pitch</span>
          </div>
        </div>
      </div>
      <div style="background:${fk.lichtBg}; border:1px solid ${fk.rand}; border-top:none; padding:7px 14px; margin-bottom:1px;">
        <span style="font-size:11px; color:${fk.tekst}; font-style:italic;">${data.intro}</span>
      </div>
      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:#f5f4f1;">
            <th style="width:55%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none;">Toetsvraag</th>
            <th style="width:45%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none; border-left:none;">Reactie / notitie</th>
          </tr>
        </thead>
        <tbody>`;

    Object.entries(data.presentatie).forEach(([groep, vragen]) => {
      const groepLabel = P_GROEP_LABELS[groep] || groep;
      html += `
          <tr style="background:${fk.bg};">
            <td colspan="2" style="padding:4px 10px; border:1px solid ${fk.rand}; border-top:none;">
              <span style="font-size:9px; font-weight:700; color:${fk.tekst}; text-transform:uppercase; letter-spacing:0.1em;">${groepLabel}</span>
            </td>
          </tr>`;
      vragen.forEach((v, i) => {
        const bg = i % 2 === 0 ? '#ffffff' : '#fafaf8';
        html += `
          <tr style="background:${bg}; vertical-align:top; page-break-inside:avoid;">
            <td style="padding:6px 10px; border:1px solid #edecea; border-top:none;">
              <span style="font-size:9px; font-weight:700; color:#c0aee0; margin-right:5px;">${String(nr).padStart(2,'0')}</span><span style="font-family:Georgia,serif; font-size:12px; color:#1e1c17; line-height:1.45;">${v.vraag}</span>
            </td>
            <td style="padding:6px 10px; border:1px solid #edecea; border-top:none; border-left:none; background:#fdfdf9;"></td>
          </tr>`;
        nr++;
      });
    });
    html += `</tbody></table>`;
    return html;
  };

  return `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>TULP Vragenlijst — ${form.klantBedrijf || 'Klant'}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600&display=swap');
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'DM Sans',sans-serif; color:#1e1c17; background:#fff; font-size:12px; line-height:1.4; }
  @media print {
    @page { margin:12mm 10mm; size:A4 portrait; }
    .no-print { display:none !important; }
    body { font-size:10px; }
    tr { page-break-inside:avoid; }
    table { page-break-inside:auto; }
  }
</style>
</head>
<body>

<!-- PRINT KNOP -->
<div class="no-print" style="position:fixed; top:12px; right:12px; z-index:999; display:flex; gap:8px;">
  <button onclick="window.print()" style="background:#cbad74; color:#fff; border:none; border-radius:6px; padding:9px 18px; font-size:13px; font-weight:600; cursor:pointer; font-family:'DM Sans',sans-serif; box-shadow:0 2px 8px rgba(168,137,79,.3);">
    🖨 Afdrukken / Opslaan als PDF
  </button>
  <button onclick="window.close()" style="background:#f5f4f1; color:#6b6860; border:none; border-radius:6px; padding:9px 14px; font-size:13px; cursor:pointer; font-family:'DM Sans',sans-serif;">
    ✕
  </button>
</div>

<!-- HEADER -->
<div style="padding:14px 24px 12px; border-bottom:2px solid #cbad74; display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
  <div style="display:flex; align-items:center; gap:16px;">
    <div>
     <div style="display:flex; align-items:center; gap:8px; margin-bottom:2px;">${logoSrc ? `<img src="${logoSrc}" alt="Tulpiaan" style="width:24px; height:24px; object-fit:contain;" />` : `<span style="color:#cbad74; font-size:16px;">✦</span>`}<span style="font-size:9px; letter-spacing:0.3em; color:#a8894f; font-weight:700; text-transform:uppercase;">TULPIAAN</span></div>
      <div style="font-family:Georgia,serif; font-size:18px; font-weight:600; color:#1e1c17; letter-spacing:-0.02em;">Gespreksvoorbereiding</div>
    </div>
    <div style="width:1px; height:32px; background:#edecea;"></div>
    <div style="display:flex; gap:20px; font-size:11px;">
      <div><span style="color:#9e9b93;">Adviseur:</span> <strong>${form.eigenBedrijf || 'Tulpiaan'}</strong></div>
      <div><span style="color:#9e9b93;">Klant:</span> <strong>${form.klantBedrijf || 'Niet opgegeven'}</strong></div>
      <div><span style="color:#9e9b93;">Gesprek:</span> <strong>${form.gesprekspartnerRol}</strong></div>
      <div><span style="color:#9e9b93;">Branche:</span> <strong>${form.klantBranche}</strong></div>
    </div>
  </div>
  <div style="text-align:right; font-size:10px; color:#9e9b93;">${datum}</div>
</div>

<div style="padding:0 24px 24px;">

<!-- SAMENVATTING -->
<div style="background:#fdf9f0; border-left:3px solid #cbad74; padding:10px 14px; margin-bottom:12px; display:flex; gap:12px; align-items:flex-start;">
  <div style="font-size:9px; font-weight:700; color:#a8894f; text-transform:uppercase; letter-spacing:0.12em; white-space:nowrap; padding-top:2px;">Gespreksstrategie</div>
  <div style="font-family:Georgia,serif; font-size:12px; line-height:1.6; color:#3a3830; font-style:italic;">${tulData.samenvatting}</div>
</div>

<!-- TUL SECTIES -->
${renderTulSectie('T','Toekomst','Waar wil de klant naartoe?', tulData.toekomst, TUL_GROEP_LABELS)}
${renderTulSectie('U','Uitdagingen','Wat staat er tussen nu en de toekomst?', tulData.uitdagingen, TUL_GROEP_LABELS)}
${renderTulSectie('L','Levering','Kunnen ze het waarmaken en hoe werken we samen?', tulData.levering, TUL_GROEP_LABELS)}

${pData ? renderPSectie(pData) : ''}

<!-- FOOTER -->
<div style="margin-top:32px; padding-top:16px; border-top:1px solid #edecea; display:flex; justify-content:space-between; align-items:center;">
  <div style="font-size:10px; color:#9e9b93;">✦ Tulpiaan Sales Training — TULP-methode</div>
  <div style="font-size:10px; color:#9e9b93;">${datum} — Vertrouwelijk</div>
</div>

</div>
</body>
</html>`;
}

// =============================================================================
// EMAIL TEKST GENERATOR
// =============================================================================
function genereerMailTekst(form, tulData, pData) {
  const datum = new Date().toLocaleDateString('nl-NL', {day:'numeric', month:'long', year:'numeric'});
  let tekst = '';
  tekst += `TULPIAAN GESPREKSVOORBEREIDING\n`;
  tekst += `${form.klantBedrijf || 'Klant'} — ${form.gesprekspartnerRol} — ${datum}\n`;
  tekst += `${'═'.repeat(60)}\n\n`;
  tekst += `GESPREKSSTRATEGIE\n${tulData.samenvatting}\n\n`;

  let globalNr = 1;
  TUL_CATS.forEach(cat => {
    tekst += `\n\n${cat.letter} | ${cat.label.toUpperCase()}\n`;
    Object.entries(tulData[cat.key] || {}).forEach(([groep, vragen]) => {
      tekst += `\n  ${(TUL_GROEP_LABELS[groep] || groep).toUpperCase()}\n`;
      vragen.forEach(v => {
        tekst += `\n  ${String(globalNr).padStart(2,'0')}. ${v.vraag}\n`;
        tekst += `\n\n`;
        globalNr++;
      });
    });
  });

  if (pData) {
    tekst += `\n\nP | PRESENTATIE — Toetsvragen na de pitch\n`;
    tekst += `${pData.intro}\n`;
    let pNr = 1;
    Object.entries(pData.presentatie).forEach(([groep, vragen]) => {
      tekst += `\n  ${(P_GROEP_LABELS[groep] || groep).toUpperCase()}\n`;
      vragen.forEach(v => {
        tekst += `\n  ${String(pNr).padStart(2,'0')}. ${v.vraag}\n`;
        pNr++;
      });
    });
  }

  tekst += `\n\nTulpiaan Sales Training — TULP-methode\n`;
  return tekst;
}

// =============================================================================
// EXCEL GENERATOR (SheetJS) — opmaak conform voorbeeldbestand
// =============================================================================
function genereerExcel(form, tulData, pData) {
  const XLSX = window.XLSX;
  if (!XLSX) { alert('Excel-library laadt nog, probeer opnieuw.'); return; }

  // eslint-disable-next-line no-unused-vars
  const datum = new Date().toLocaleDateString('nl-NL', {day:'numeric', month:'long', year:'numeric'});
  const wb = XLSX.utils.book_new();
  const rijen = [];

  // Rij 1: Titel (kolom B)
  rijen.push(['', 'TULPIAAN — Gespreksvoorbereiding', '']);
  // Rij 2: Samenvatting (kolom B, hoge rij)
  rijen.push(['', tulData.samenvatting, '']);
  // Rij 3: leeg
  rijen.push(['', '', '']);

  let nr = 1;

  const faseTitels = {
    toekomst:    'TOEKOMST — Waar wil de klant naartoe?',
    uitdagingen: 'UITDAGINGEN — Wat staat er tussen nu en de toekomst?',
    levering:    'LEVERING — Kunnen ze het waarmaken en hoe werken we samen?',
  };
  const faseLetter = { toekomst: 'T', uitdagingen: 'U', levering: 'L' };

  TUL_CATS.forEach(cat => {
    // Fase-header: letter in kolom A, titel in kolom B, "Antwoord:" in kolom C
    rijen.push([faseLetter[cat.key], faseTitels[cat.key], 'Antwoord:']);
    Object.entries(tulData[cat.key] || {}).forEach(([groep, vragen]) => {
      vragen.forEach(v => {
        rijen.push([String(nr).padStart(2, '0'), v.vraag, '']);
        nr++;
      });
    });
  });

  if (pData) {
    rijen.push(['P', 'PRESENTATIE — Toetsvragen na de pitch', 'Antwoord:']);
    let pNr = nr;
    Object.entries(pData.presentatie).forEach(([groep, vragen]) => {
      vragen.forEach(v => {
        rijen.push([String(pNr).padStart(2, '0'), v.vraag, '']);
        pNr++;
      });
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(rijen);

  // Kolombreedte conform voorbeeldbestand
  ws['!cols'] = [{ wch: 4.3 }, { wch: 61 }, { wch: 41 }];

  // Rijhoogte rij 2 (samenvatting) hoog maken
  if (!ws['!rows']) ws['!rows'] = [];
  ws['!rows'][1] = { hpt: 67 };

  // Vet maken: fase-headers (letter + titel)
  const vetRijen = [];
  rijen.forEach((r, i) => {
    if (r[0] && ['T','U','L','P'].includes(r[0])) vetRijen.push(i);
  });

  const getCellRef = (r, c) => XLSX.utils.encode_cell({ r, c });
  vetRijen.forEach(ri => {
    [0, 1].forEach(ci => {
      const ref = getCellRef(ri, ci);
      if (ws[ref]) {
        ws[ref].s = { font: { bold: true, name: 'Arial', sz: 11 } };
      }
    });
  });

  // Titel rij 1 ook vet
  const titelRef = getCellRef(0, 1);
  if (ws[titelRef]) ws[titelRef].s = { font: { bold: true, name: 'Arial', sz: 12 } };

  XLSX.utils.book_append_sheet(wb, ws, 'TULP Vragenlijst');
  const bestandsnaam = `TULP_${(form.klantBedrijf || 'Klant').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.xlsx`;
  XLSX.writeFile(wb, bestandsnaam);
}

// =============================================================================
// EXPORT PANEEL COMPONENT
// =============================================================================
function ExportPaneel({ form, tulData, pData }) {
  const [gekopieerd, setGekopieerd] = useState(false);
  const [xlsxKlaar, setXlsxKlaar] = useState(!!window.XLSX);

  useEffect(() => {
    if (!window.XLSX) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      script.onload = () => setXlsxKlaar(true);
      document.head.appendChild(script);
    }
  }, []);

  const openPdf = () => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext('2d').drawImage(img, 0, 0);
    const b64 = canvas.toDataURL('image/png');
    const html = genereerPdfHtml(form, tulData, pData, b64);
    const venster = window.open('', '_blank', 'width=1200,height=800');
    if (venster) { venster.document.write(html); venster.document.close(); }
  };
  img.onerror = () => {
    const html = genereerPdfHtml(form, tulData, pData, null);
    const venster = window.open('', '_blank', 'width=1200,height=800');
    if (venster) { venster.document.write(html); venster.document.close(); }
  };
  img.src = logo;
};

  const downloadExcel = () => genereerExcel(form, tulData, pData);

  const kopieerAlles = () => {
    navigator.clipboard.writeText(genereerMailTekst(form, tulData, pData));
    setGekopieerd(true);
    setTimeout(() => setGekopieerd(false), 2000);
  };

  return (
    <div style={xp.paneel}>
      <div style={xp.paneelKop}>
        <span style={xp.paneelIcon}>↗</span>
        <div>
          <div style={xp.paneelTitel}>Exporteren</div>
          <div style={xp.paneelSub}>PDF voor voorbereiding · Excel voor tijdens het gesprek · Kopieer voor CRM</div>
        </div>
      </div>

      <div style={xp.rijen}>

        {/* PDF */}
        <div style={xp.rij}>
          <div style={xp.rijInfo}>
            <div style={xp.rijTitel}>📄 PDF — Volledige voorbereiding</div>
            <div style={xp.rijSub}>Met samenvatting, alle vragen, waarom en antwoordkolom. Tulpiaan-opmaak. Afdrukken of opslaan als PDF.</div>
          </div>
          <button onClick={openPdf} style={xp.knopGoud} className="knop-outline">
            Openen / Afdrukken
          </button>
        </div>

        <div style={xp.divider}></div>

        {/* EXCEL */}
        <div style={xp.rij}>
          <div style={xp.rijInfo}>
            <div style={xp.rijTitel}>📊 Excel — Gespreksversie</div>
            <div style={xp.rijSub}>Alleen vragen en lege antwoordkolom. Invullen tijdens het gesprek, doorsturen naar collega of CRM.</div>
          </div>
          <button
            onClick={downloadExcel}
            disabled={!xlsxKlaar}
            style={{ ...xp.knopGoud, opacity: xlsxKlaar ? 1 : 0.45, cursor: xlsxKlaar ? 'pointer' : 'not-allowed' }}
            className="knop-outline"
          >
            {xlsxKlaar ? 'Download .xlsx' : 'Laden...'}
          </button>
        </div>

        <div style={xp.divider}></div>

        {/* KOPIEER */}
        <div style={xp.rij}>
          <div style={xp.rijInfo}>
            <div style={xp.rijTitel}>📋 Kopieer — Platte tekst</div>
            <div style={xp.rijSub}>Plak direct in Notion, Word, je CRM of een andere tool.</div>
          </div>
          <button onClick={kopieerAlles} style={xp.knopGhostXp} className="knop-ghost">
            {gekopieerd ? '✓ Gekopieerd' : 'Kopieer'}
          </button>
        </div>

      </div>
    </div>
  );
}

const xp = {
  paneel: { background: C.wit, border: `1.5px solid ${C.grijs200}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.06)', marginTop: 32 },
  paneelKop: { background: C.grijs50, borderBottom: `1px solid ${C.grijs100}`, padding: '14px 22px', display: 'flex', alignItems: 'center', gap: 12 },
  paneelIcon: { fontSize: 18, color: C.goudDiep, fontWeight: 700 },
  paneelTitel: { fontSize: 14, fontWeight: 700, color: C.inkt },
  paneelSub: { fontSize: 12, color: C.grijs400 },
  rijen: { padding: '4px 0' },
  rij: { padding: '16px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' },
  rijInfo: { flex: 1, minWidth: 200 },
  rijTitel: { fontSize: 13, fontWeight: 600, color: C.inkt, marginBottom: 3 },
  rijSub: { fontSize: 12, color: C.grijs400, lineHeight: 1.5 },
  divider: { height: 1, background: C.grijs100, margin: '0 22px' },
  knopGoud: { border: `1.5px solid ${C.goud}`, color: C.goudDiep, background: 'transparent', borderRadius: 7, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all .15s ease', whiteSpace: 'nowrap' },
  knopGhostXp: { border: `1.5px solid ${C.grijs200}`, color: C.grijs600, background: 'transparent', borderRadius: 7, padding: '9px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all .15s ease', whiteSpace: 'nowrap' },
};

// =============================================================================
// GLOBALE CSS
// =============================================================================
const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  input, textarea, button { font-family:'DM Sans',sans-serif; }
  input::placeholder, textarea::placeholder { color:${C.grijs400}; }
  input:focus, textarea:focus { outline:none; border-color:${C.goud} !important; box-shadow:0 0 0 3px ${C.goudLicht} !important; }
  textarea { resize:vertical; }
  @keyframes spin  { to { transform:rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
  .kaart:hover      { border-color:${C.goud} !important; box-shadow:0 2px 12px rgba(203,173,116,.12) !important; }
  .vraagkaart:hover { transform:translateY(-1px); box-shadow:0 4px 16px rgba(0,0,0,.07) !important; }
  .tab-btn:hover:not(.tab-actief) { background:${C.grijs100} !important; }
  .knop-primair:hover:not(:disabled) { background:${C.goudDiep} !important; box-shadow:0 4px 20px rgba(168,137,79,.35) !important; }
  .knop-primair:disabled { opacity:.45; cursor:not-allowed; }
  .knop-outline:hover { background:${C.goud} !important; color:white !important; }
  .knop-ghost:hover   { background:${C.grijs100} !important; color:${C.grijs800} !important; }
`;

// =============================================================================
// HOOFDCOMPONENT
// =============================================================================
export default function TulpTool() {
  const [stap, setStap]       = useState('intake');
  const [fout, setFout]       = useState(null);
  const [tulData, setTulData] = useState(null);
  const [pData, setPData]     = useState(null);
  const [form, setForm]       = useState({
    eigenBedrijf:'', eigenAanbod:'', klantBedrijf:'', klantBranche:'',
    klantAanbod:'', gesprekspartnerRol:'', complexiteit:'middel', aanleiding:'', websiteUrl:'',
  });

  const update = (k,v) => setForm(p=>({...p,[k]:v}));
  const formValid = form.eigenAanbod.trim() && form.klantBranche.trim() && form.gesprekspartnerRol.trim();

  const contextString = `
Adviseur werkt bij: ${form.eigenBedrijf||'niet opgegeven'}
Adviseur verkoopt: ${form.eigenAanbod}
Klant: ${form.klantBedrijf||'niet opgegeven'} (${form.klantBranche})
Wat de klant doet: ${form.klantAanbod||'niet opgegeven'}
${form.websiteUrl?'Website: '+form.websiteUrl:''}
Gesprekspartner: ${form.gesprekspartnerRol}
Complexiteit: ${form.complexiteit}
${form.aanleiding?'Aanleiding: '+form.aanleiding:''}`.trim();

  const roepApi = async (prompt) => {
    const token = await getTulpiaanToken();
    const res = await fetch('/api/claude', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tulpiaan-token': token,
      },
      body: JSON.stringify({
        // Geen model hier: de server (api/claude.js) bepaalt het model.
        max_tokens: 4000,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) throw new Error('API ' + res.status);
    const data = await res.json();
    const raw = data.content.filter(b => b.type === 'text').map(b => b.text).join('');
    return JSON.parse(raw.replace(/```json|```/g, '').trim());
  };

  const genereerTUL = async () => {
    setStap('laden-tul'); setFout(null);
    try {
      const d = await roepApi(TUL_PROMPT+'\n\n'+contextString+'\n\nGenereer nu de TUL-vragenlijst. Informeel, direct, conversationeel. Alleen JSON.');
      setTulData(d); setStap('tul');
    } catch(e) { setFout('Er ging iets mis: '+e.message); setStap('intake'); }
  };

  const genereerP = async () => {
    setStap('laden-p'); setFout(null);
    try {
      const d = await roepApi(P_PROMPT+'\n\nCONTEXT:\n'+contextString+'\n\nTUL:\n'+JSON.stringify(tulData,null,2)+'\n\nGenereer P-fase. Informeel, direct. Alleen JSON.');
      setPData(d); setStap('p');
    } catch(e) { setFout('Er ging iets mis: '+e.message); setStap('tul'); }
  };

  const reset = () => { setTulData(null); setPData(null); setStap('intake'); };

  return (
    <>
      <style>{globalCss}</style>
      <div style={s.page}>
        <Header />
        <main style={s.main}>
          {stap !== 'intake' && <VoortgangsBalk stap={stap} />}
          {stap==='intake'    && <Intake form={form} update={update} formValid={formValid} genereer={genereerTUL} fout={fout} />}
          {stap==='laden-tul' && <LaadScherm fase="TUL" />}
          {stap==='tul'       && <TulResultaat data={tulData} form={form} genereerP={genereerP} reset={reset} fout={fout} pData={pData} />}
          {stap==='laden-p'   && <LaadScherm fase="P" />}
          {stap==='p'         && <PResultaat data={pData} form={form} tulData={tulData} reset={reset} terugNaarTul={()=>setStap('tul')} />}
        </main>
        <footer style={s.footer}>
          <span>TULP-methode</span><span style={{color:C.grijs200}}>·</span><span>Tulpiaan Sales Training</span>
        </footer>
      </div>
    </>
  );
}

// =============================================================================
// HEADER
// =============================================================================
function Header() {
  return (
    <header style={s.header}>
      <div style={s.headerInner}>
        <div style={s.merk}><img src={logo} alt="Tulpiaan logo" style={{ width: 32, height: 32, objectFit: 'contain' }} /><span style={s.merkNaam}>TULPIAAN</span></div>
        <div>
          <h1 style={s.h1}>Gespreksvoorbereiding</h1>
          <p style={s.tagline}>Inventariseren en presenteren met de <em style={s.em}>TULP-methode</em></p>
        </div>
      </div>
    </header>
  );
}

// =============================================================================
// VOORTGANGSBALK
// =============================================================================
function VoortgangsBalk({ stap }) {
  const fases = [
    {key:'intake', label:'Context',        letter:'→'},
    {key:'tul',    label:'Inventarisatie', letter:'TUL'},
    {key:'p',      label:'Presentatie',    letter:'P'},
  ];
  const actief = stap.startsWith('laden-tul')?'tul':stap.startsWith('laden-p')?'p':stap;
  const volgorde = ['intake','tul','p'];
  const actiefIdx = volgorde.indexOf(actief);

  return (
    <div style={s.voortgangsBalk}>
      {fases.map((f,i) => {
        const gedaan = i < actiefIdx, huidig = f.key===actief;
        return (
          <React.Fragment key={f.key}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{...s.voortgangsCirkel,...(huidig?{background:C.goud,color:C.wit,borderColor:C.goud}:{}),...(gedaan?{background:C.goudLicht,color:C.goudDiep,borderColor:C.goudLicht}:{})}}>
                {gedaan?'✓':f.letter}
              </div>
              <span style={{...s.voortgangsLabel,...(huidig?{color:C.inkt,fontWeight:600}:{})}}>{f.label}</span>
            </div>
            {i<fases.length-1 && <div style={s.voortgangsLijn}></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// =============================================================================
// INTAKE
// =============================================================================
function Intake({ form, update, formValid, genereer, fout }) {
  return (
    <div style={{animation:'fadeUp .35s ease'}}>
      <div style={s.grid2}>
        <div style={s.kolom}>
          <FormSectie icon="💼" titel="Jouw bedrijf en aanbod">
            <Veld label="Bedrijfsnaam" placeholder="Bijv. Tulpiaan Recruitment" waarde={form.eigenBedrijf} onChange={v=>update('eigenBedrijf',v)} />
            <Veld label="Wat verkoop je?" placeholder="Bijv. salestrainingen voor B2B-teams" waarde={form.eigenAanbod} onChange={v=>update('eigenAanbod',v)} verplicht multiline hint="Wees specifiek, dit bepaalt de richting van de vragen" />
          </FormSectie>
          <FormSectie icon="🏢" titel="De klant">
            <Veld label="Bedrijfsnaam" placeholder="Bijv. Van Dijk Machinebouw BV" waarde={form.klantBedrijf} onChange={v=>update('klantBedrijf',v)} />
            <Veld label="Branche" placeholder="Bijv. machinebouw, SaaS, zorg" waarde={form.klantBranche} onChange={v=>update('klantBranche',v)} verplicht />
            <Veld label="Wat doen zij en aan wie?" placeholder="Bijv. maken verpakkingsmachines voor de voedingsindustrie" waarde={form.klantAanbod} onChange={v=>update('klantAanbod',v)} multiline />
            <Veld label="Website of product-URL" placeholder="https://..." waarde={form.websiteUrl} onChange={v=>update('websiteUrl',v)} hint="Optioneel, wordt meegenomen als extra context" />
          </FormSectie>
        </div>
        <div style={s.kolom}>
          <FormSectie icon="🙋" titel="De gesprekspartner">
            <Veld label="Functie of rol" placeholder="Bijv. HR-directeur, CFO, DGA" waarde={form.gesprekspartnerRol} onChange={v=>update('gesprekspartnerRol',v)} verplicht />
          </FormSectie>
          <FormSectie icon="📊" titel="Complexiteit">
            <div style={s.radioGroep}>
              {[{w:'laag',icon:'●',label:'Laag',sub:'Korte cyclus, 1 beslisser'},{w:'middel',icon:'●●',label:'Middel',sub:'Meerdere stakeholders'},{w:'hoog',icon:'●●●',label:'Hoog',sub:'DMU, lange cyclus, groot budget'}].map(opt=>(
                <button key={opt.w} className="kaart" onClick={()=>update('complexiteit',opt.w)}
                  style={{...s.radioBtn,...(form.complexiteit===opt.w?s.radioBtnAan:{})}}>
                  <div style={{...s.radioIcon,...(form.complexiteit===opt.w?{color:C.goud}:{})}}>{opt.icon}</div>
                  <div><div style={s.radioLabel}>{opt.label}</div><div style={s.radioSub}>{opt.sub}</div></div>
                </button>
              ))}
            </div>
          </FormSectie>
          <FormSectie icon="💡" titel="Aanleiding (optioneel)">
            <Veld label="Jouw hypothese over hun situatie" placeholder="Bijv. ze willen groeien maar hun salesteam haalt de targets niet" waarde={form.aanleiding} onChange={v=>update('aanleiding',v)} multiline />
          </FormSectie>
        </div>
      </div>
      <div style={s.verplichtRij}><span style={{color:C.goud}}>*</span> Verplicht: wat je verkoopt, branche van de klant en rol van de gesprekspartner</div>
      {fout && <div style={s.foutBox}>{fout}</div>}
      <div style={s.actieRij}>
        <button className="knop-primair" disabled={!formValid} onClick={genereer} style={s.knopPrimair}>
          Genereer TUL-vragenlijst <span style={{fontSize:18}}>→</span>
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// TUL RESULTAAT
// =============================================================================
function TulResultaat({ data, form, genereerP, reset, fout, pData }) {
  const [actief, setActief] = useState('toekomst');
  const totaal = TUL_CATS.reduce((a,c)=>a+Object.values(data[c.key]||{}).reduce((b,arr)=>b+arr.length,0),0);
  const actieveCat = TUL_CATS.find(c=>c.key===actief);
  const fk = FASE_KLEUR[actief]||{};

  return (
    <div style={{animation:'fadeUp .35s ease'}}>
      <div style={s.resultaatHeader}>
        <div>
          <div style={s.resultaatBadge}>{totaal} inventarisatievragen</div>
          <h2 style={s.resultaatH2}>{form.klantBedrijf||'Vragenlijst'}<span style={{color:C.grijs400,fontWeight:300}}> — {form.gesprekspartnerRol}</span></h2>
        </div>
        <div style={s.resultaatActies}>
          <button className="knop-ghost" onClick={reset} style={s.knopGhost}>← Opnieuw</button>
        </div>
      </div>

      <div style={s.samenvattingBox}>
        <div style={s.samenvattingKop}><span style={s.samenvattingDot}></span>Gespreksstrategie</div>
        <p style={s.samenvattingTekst}>{data.samenvatting}</p>
      </div>

      <div style={s.tabBar}>
        {TUL_CATS.map(c=>{
          const n = Object.values(data[c.key]||{}).reduce((a,arr)=>a+arr.length,0);
          const cfk = FASE_KLEUR[c.key];
          return (
            <button key={c.key} className={'tab-btn'+(actief===c.key?' tab-actief':'')}
              onClick={()=>setActief(c.key)}
              style={{...s.tab,...(actief===c.key?{background:cfk.bg,border:`1.5px solid ${cfk.rand}`}:{})}}>
              <span style={{...s.tabLetter,color:actief===c.key?cfk.tekst:C.grijs300}}>{c.letter}</span>
              <div><div style={{...s.tabLabel,color:actief===c.key?cfk.tekst:C.grijs600}}>{c.label}</div><div style={s.tabSub}>{n} vragen</div></div>
            </button>
          );
        })}
      </div>

      <div style={{animation:'fadeUp .25s ease'}}>
        <div style={{...s.catHeader,borderColor:fk.rand||C.grijs100}}>
          <div style={{...s.catLetterBig,color:fk.tekst||C.goud}}>{actieveCat.letter}</div>
          <div><h3 style={s.catTitel}>{actieveCat.label}</h3><p style={s.catSub}>{actieveCat.sub}</p></div>
        </div>
        {Object.entries(data[actief]||{}).map(([groep,vragen])=>(
          <Groep key={groep} naam={TUL_GROEP_LABELS[groep]||groep} vragen={vragen} accentKleur={fk.tekst} />
        ))}
      </div>

      {/* EXPORT PANEEL */}
      <ExportPaneel form={form} tulData={data} pData={pData} />

      {/* P-FASE BANNER */}
      {!pData && (
        <div style={s.pFaseBanner}>
          <div>
            <div style={s.pFaseBannerTitel}>Klaar met inventariseren?</div>
            <div style={s.pFaseBannerSub}>Genereer de toetsvragen voor na je presentatie</div>
          </div>
          <button onClick={genereerP} style={s.knopP}>Ga naar P-fase →</button>
        </div>
      )}
      {pData && (
        <div style={{...s.pFaseBanner, background:C.lGroen, borderColor:C.lGroenRand}}>
          <div>
            <div style={{...s.pFaseBannerTitel, color:C.lTekst}}>✓ P-fase is gegenereerd</div>
            <div style={s.pFaseBannerSub}>De toetsvragen zijn ook meegenomen in de export</div>
          </div>
          <button onClick={()=>{}} style={{...s.knopP, background:C.lTekst}} className="knop-ghost">Bekijk P-fase →</button>
        </div>
      )}

      {fout && <div style={{...s.foutBox,marginTop:16}}>{fout}</div>}
    </div>
  );
}

// =============================================================================
// P RESULTAAT
// =============================================================================
function PResultaat({ data, form, tulData, reset, terugNaarTul }) {
  const totaal = Object.values(data.presentatie||{}).reduce((a,arr)=>a+arr.length,0);
  const fk = FASE_KLEUR.presentatie;

  return (
    <div style={{animation:'fadeUp .35s ease'}}>
      <div style={s.resultaatHeader}>
        <div>
          <div style={{...s.resultaatBadge,background:fk.bg,color:fk.tekst}}>{totaal} toetsvragen</div>
          <h2 style={s.resultaatH2}>P — Presentatiefase<span style={{color:C.grijs400,fontWeight:300}}> — {form.klantBedrijf||'Klant'}</span></h2>
        </div>
        <div style={s.resultaatActies}>
          <button className="knop-ghost" onClick={terugNaarTul} style={s.knopGhost}>← Terug naar TUL</button>
          <button className="knop-ghost" onClick={reset} style={s.knopGhost}>Opnieuw</button>
        </div>
      </div>

      <div style={{...s.samenvattingBox,borderLeftColor:fk.rand,background:fk.bg}}>
        <div style={{...s.samenvattingKop,color:fk.tekst}}><span style={{...s.samenvattingDot,background:fk.tekst}}></span>Hoe je de P-fase introduceert</div>
        <p style={{...s.samenvattingTekst,color:fk.tekst}}>{data.intro}</p>
      </div>

      {Object.entries(data.presentatie||{}).map(([groep,vragen])=>(
        <Groep key={groep} naam={P_GROEP_LABELS[groep]||groep} vragen={vragen} accentKleur={fk.tekst} />
      ))}

      {/* EXPORT PANEEL */}
      <ExportPaneel form={form} tulData={tulData} pData={data} />
    </div>
  );
}

// =============================================================================
// GEDEELDE COMPONENTEN
// =============================================================================
function Groep({ naam, vragen, accentKleur }) {
  return (
    <div style={s.groep}>
      <div style={s.groepKop}>
        <div style={{...s.groepDot,background:accentKleur||C.goud}}></div>
        <h4 style={s.groepTitel}>{naam}</h4>
        <div style={s.groepLijn}></div>
        <span style={{...s.groepTeller,color:accentKleur||C.goudDiep,background:(accentKleur||C.goudDiep)+'18'}}>{vragen.length}</span>
      </div>
      <div>{vragen.map((v,i)=><VraagKaart key={i} nr={i+1} vraag={v.vraag} waarom={v.waarom} accentKleur={accentKleur} />)}</div>
    </div>
  );
}

function VraagKaart({ nr, vraag, waarom, accentKleur }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="vraagkaart"
      style={{...s.vraagKaart,...(open?{border:`1px solid ${C.grijs200}`,boxShadow:'0 3px 12px rgba(0,0,0,.08)'}:{})}}
      onClick={()=>setOpen(o=>!o)}>
      <div style={s.vraagTop}>
        <span style={s.vraagNr}>{String(nr).padStart(2,'0')}</span>
        <p style={s.vraagTekst}>{vraag}</p>
        <span style={{...s.chevron,...(open?{transform:'rotate(180deg)'}:{})}}>▾</span>
      </div>
      {open && (
        <div style={s.waaromBlok}>
          <span style={{...s.waaromLabel,color:accentKleur||C.goudDiep}}>Waarom je dit vraagt</span>
          <p style={s.waaromTekst}>{waarom}</p>
        </div>
      )}
    </div>
  );
}

function FormSectie({ icon, titel, children }) {
  return (
    <div style={s.sectie}>
      <div style={s.sectieKop}><span style={s.sectieIcon}>{icon}</span><span style={s.sectieTitel}>{titel}</span></div>
      <div style={s.sectieBody}>{children}</div>
    </div>
  );
}

function Veld({ label, waarde, onChange, placeholder, verplicht, multiline, hint }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div style={s.veld}>
      <label style={s.label}>{label}{verplicht&&<span style={{color:C.goud}}> *</span>}</label>
      <Tag value={waarde} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={2}
        style={{...s.input,...(multiline?{minHeight:64,lineHeight:1.5}:{})}} />
      {hint&&<span style={s.hint}>{hint}</span>}
    </div>
  );
}

function LaadScherm({ fase }) {
  const regels = fase==='TUL'
    ? ['Context analyseren','Branchespecifieke vragen opstellen','Toon afstemmen op de rol','Samenwerking in kaart brengen']
    : ['TUL-gesprek analyseren','Verbindingen leggen met eerder gezegde','Toetsvragen opstellen'];
  const [idx,setIdx] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ const id=setInterval(()=>setIdx(i=>(i+1)%regels.length),1800); return()=>clearInterval(id); },[]);
  return (
    <div style={s.laden}>
      <div style={s.ladenCard}>
        <div style={s.spinner}></div>
        <p style={s.ladenTitel}>{fase==='TUL'?'TUL-vragenlijst genereren':'P-fase toetsvragen genereren'}</p>
        <p style={s.ladenSub}>{regels[idx]}</p>
        <div style={s.voortbalkWrap}><div style={s.voortbalk}></div></div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================
const s = {
  page:{minHeight:'100vh',background:C.papier,fontFamily:"'DM Sans',sans-serif",color:C.inkt,display:'flex',flexDirection:'column'},
  header:{background:C.wit,borderBottom:`1px solid ${C.grijs100}`,padding:'0 32px'},
  headerInner:{maxWidth:960,margin:'0 auto',padding:'24px 0',display:'flex',alignItems:'center',gap:24},
  merk:{display:'flex',flexDirection:'column',alignItems:'center',gap:4,borderRight:`1px solid ${C.grijs200}`,paddingRight:24,minWidth:80},
  merkTulp:{fontSize:20,color:C.goud}, merkNaam:{fontSize:10,letterSpacing:'0.25em',color:C.grijs600,fontWeight:600,textTransform:'uppercase'},
  h1:{fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,letterSpacing:'-0.02em',color:C.inkt},
  tagline:{fontSize:13,color:C.grijs600,marginTop:3}, em:{color:C.goudDiep,fontStyle:'italic'},
  main:{flex:1,maxWidth:960,width:'100%',margin:'0 auto',padding:'36px 32px'},
  footer:{textAlign:'center',padding:'18px 32px',fontSize:12,color:C.grijs400,borderTop:`1px solid ${C.grijs100}`,display:'flex',justifyContent:'center',gap:12},
  voortgangsBalk:{display:'flex',alignItems:'center',marginBottom:36,background:C.wit,border:`1px solid ${C.grijs100}`,borderRadius:10,padding:'14px 24px',boxShadow:'0 1px 4px rgba(0,0,0,.04)'},
  voortgangsCirkel:{width:32,height:32,borderRadius:'50%',border:`2px solid ${C.grijs200}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:C.grijs400,flexShrink:0},
  voortgangsLabel:{fontSize:13,color:C.grijs400},
  voortgangsLijn:{flex:1,height:1,background:C.grijs200,margin:'0 12px'},
  grid2:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))',gap:20,alignItems:'start'},
  kolom:{display:'flex',flexDirection:'column',gap:16},
  sectie:{background:C.wit,border:`1px solid ${C.grijs100}`,borderRadius:10,overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,.04)'},
  sectieKop:{display:'flex',alignItems:'center',gap:10,padding:'13px 18px',borderBottom:`1px solid ${C.grijs100}`,background:C.grijs50},
  sectieIcon:{fontSize:15}, sectieTitel:{fontSize:13,fontWeight:600,color:C.grijs800},
  sectieBody:{padding:'16px 18px',display:'flex',flexDirection:'column',gap:13},
  veld:{display:'flex',flexDirection:'column',gap:5},
  label:{fontSize:12,fontWeight:600,color:C.grijs600,letterSpacing:'0.02em'},
  input:{border:`1.5px solid ${C.grijs200}`,borderRadius:7,padding:'10px 12px',fontSize:13,color:C.inkt,background:C.wit,transition:'all .15s ease',width:'100%'},
  hint:{fontSize:11,color:C.grijs400,fontStyle:'italic'},
  radioGroep:{display:'flex',flexDirection:'column',gap:7},
  radioBtn:{background:C.wit,border:`1.5px solid ${C.grijs200}`,borderRadius:8,padding:'11px 13px',cursor:'pointer',textAlign:'left',transition:'all .15s ease',display:'flex',alignItems:'center',gap:12},
  radioBtnAan:{borderColor:C.goud,background:'#fdf9f0',boxShadow:`0 0 0 3px ${C.goudLicht}`},
  radioIcon:{fontSize:9,color:C.grijs300,letterSpacing:-2,minWidth:20},
  radioLabel:{fontSize:13,fontWeight:600,color:C.inkt}, radioSub:{fontSize:11,color:C.grijs400},
  verplichtRij:{marginTop:16,fontSize:12,color:C.grijs400,textAlign:'right'},
  foutBox:{background:C.foutBg,border:`1px solid ${C.fout}33`,color:C.fout,padding:'12px 16px',borderRadius:8,fontSize:13,marginTop:12},
  actieRij:{marginTop:28,display:'flex',justifyContent:'center'},
  knopPrimair:{background:C.goud,color:C.wit,border:'none',borderRadius:8,padding:'13px 32px',fontSize:15,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:12,transition:'all .2s ease',boxShadow:'0 2px 12px rgba(168,137,79,.2)'},
  laden:{display:'flex',justifyContent:'center',alignItems:'center',minHeight:300},
  ladenCard:{background:C.wit,border:`1px solid ${C.grijs100}`,borderRadius:16,padding:'44px 52px',textAlign:'center',boxShadow:'0 4px 24px rgba(0,0,0,.06)',minWidth:320},
  spinner:{width:40,height:40,border:`3px solid ${C.grijs100}`,borderTopColor:C.goud,borderRadius:'50%',margin:'0 auto 20px',animation:'spin 0.9s linear infinite'},
  ladenTitel:{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:C.inkt,marginBottom:7},
  ladenSub:{fontSize:13,color:C.grijs400,animation:'pulse 1.8s ease-in-out infinite'},
  voortbalkWrap:{marginTop:22,height:3,background:C.grijs100,borderRadius:2,overflow:'hidden'},
  voortbalk:{height:'100%',width:'60%',background:`linear-gradient(90deg,${C.goudLicht},${C.goud})`,borderRadius:2,animation:'pulse 1.8s ease-in-out infinite'},
  resultaatHeader:{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:22,flexWrap:'wrap',gap:14},
  resultaatBadge:{display:'inline-block',background:C.goudLicht,color:C.goudDiep,fontSize:11,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',padding:'4px 10px',borderRadius:20,marginBottom:7},
  resultaatH2:{fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,letterSpacing:'-0.02em'},
  resultaatActies:{display:'flex',gap:7,flexWrap:'wrap'},
  knopOutline:{border:`1.5px solid ${C.goud}`,color:C.goudDiep,background:'transparent',borderRadius:7,padding:'9px 15px',fontSize:13,fontWeight:600,cursor:'pointer',transition:'all .15s ease'},
  knopGhost:{border:`1.5px solid ${C.grijs200}`,color:C.grijs600,background:'transparent',borderRadius:7,padding:'9px 15px',fontSize:13,fontWeight:500,cursor:'pointer',transition:'all .15s ease'},
  samenvattingBox:{background:C.wit,border:`1px solid ${C.grijs100}`,borderLeft:`4px solid ${C.goud}`,borderRadius:'0 10px 10px 0',padding:'18px 22px',marginBottom:28,boxShadow:'0 1px 4px rgba(0,0,0,.04)'},
  samenvattingKop:{display:'flex',alignItems:'center',gap:8,fontSize:11,fontWeight:700,color:C.goudDiep,letterSpacing:'0.12em',textTransform:'uppercase',marginBottom:9},
  samenvattingDot:{width:6,height:6,borderRadius:'50%',background:C.goud,display:'inline-block'},
  samenvattingTekst:{fontFamily:"'Lora',serif",fontSize:14,lineHeight:1.7,color:C.grijs800,fontStyle:'italic'},
  tabBar:{display:'flex',gap:8,marginBottom:24,flexWrap:'wrap'},
  tab:{flex:1,minWidth:140,display:'flex',alignItems:'center',gap:12,border:`1.5px solid ${C.grijs100}`,background:C.wit,borderRadius:9,padding:'10px 14px',cursor:'pointer',transition:'all .15s ease',color:C.grijs600},
  tabLetter:{fontFamily:"'Lora',serif",fontSize:26,fontWeight:600,lineHeight:1,color:C.grijs300},
  tabLabel:{fontSize:13,fontWeight:600}, tabSub:{fontSize:11,color:C.grijs400},
  catHeader:{display:'flex',alignItems:'center',gap:18,marginBottom:24,paddingBottom:18,borderBottom:`2px solid ${C.grijs100}`},
  catLetterBig:{fontFamily:"'Lora',serif",fontSize:56,fontWeight:600,lineHeight:1,opacity:0.3},
  catTitel:{fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:C.inkt},
  catSub:{fontSize:13,color:C.grijs400,marginTop:2},
  groep:{marginBottom:28},
  groepKop:{display:'flex',alignItems:'center',gap:9,marginBottom:10},
  groepDot:{width:7,height:7,borderRadius:'50%',flexShrink:0},
  groepTitel:{fontSize:11,fontWeight:700,color:C.grijs600,textTransform:'uppercase',letterSpacing:'0.08em'},
  groepLijn:{flex:1,height:1,background:C.grijs100},
  groepTeller:{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:10},
  vraagKaart:{background:C.wit,border:`1px solid ${C.grijs100}`,borderRadius:9,marginBottom:7,cursor:'pointer',transition:'all .15s ease',boxShadow:'0 1px 3px rgba(0,0,0,.04)',overflow:'hidden'},
  vraagTop:{display:'flex',alignItems:'flex-start',gap:13,padding:'14px 18px'},
  vraagNr:{fontFamily:"'Lora',serif",fontSize:12,fontWeight:600,color:C.grijs200,minWidth:20,paddingTop:2},
  vraagTekst:{flex:1,fontSize:15,lineHeight:1.55,color:C.inkt,fontFamily:"'Lora',serif"},
  chevron:{fontSize:13,color:C.grijs400,transition:'transform .2s ease',marginTop:3,flexShrink:0},
  waaromBlok:{borderTop:`1px solid ${C.grijs100}`,padding:'12px 18px 14px 51px',background:C.grijs50},
  waaromLabel:{display:'block',fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',marginBottom:5},
  waaromTekst:{fontSize:13,lineHeight:1.65,color:C.grijs600,fontStyle:'italic'},
  pFaseBanner:{marginTop:32,background:C.pPaars,border:`1.5px solid ${C.pPaarsRand}`,borderRadius:12,padding:'18px 22px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:16,flexWrap:'wrap'},
  pFaseBannerTitel:{fontSize:14,fontWeight:700,color:C.pTekst,marginBottom:2},
  pFaseBannerSub:{fontSize:12,color:C.grijs600},
  knopP:{background:C.pTekst,color:C.wit,border:'none',borderRadius:8,padding:'10px 20px',fontSize:13,fontWeight:600,cursor:'pointer',transition:'all .2s ease',whiteSpace:'nowrap'},
};